/**
 * sealed-vinyl 페이지 도메인 상수.
 *
 * 단가·배송비·수량 한도 모두 기획 확정값이다 (별도 상품 API 없음).
 */

import type { SealedVinylStep } from './types'

/** 1팩의 가격. 무료 나눔이라 0원 확정. */
export const SEALED_VINYL_UNIT_PRICE_KRW = 0

/** 배송비 — 확정 고정값. 결제 화면에 표시되는 실제 청구 금액. */
export const SEALED_VINYL_DELIVERY_FEE_KRW = 4500

/** 1팩 = 9장. Figma 문구 "1개 당 9장"의 원본 수치. */
export const SEALED_VINYL_SHEETS_PER_PACK = 9

/** 수량 한도 — 기획 확정 (1인 1회 최대 3개). */
export const SEALED_VINYL_MIN_QUANTITY = 1
export const SEALED_VINYL_MAX_QUANTITY = 3
export const SEALED_VINYL_DEFAULT_QUANTITY = 1

/** 재고 소진 안내 카피 (AlertModal 1버튼) — Figma 20897-28239. 판매중지·재고 0 시 노출. */
export const SOLD_OUT_DIALOG = {
  title: '비닐 재고가 소진되었어요',
  description: '현재 헌옷 비닐 재고가 모두 소진되어 신청이 불가해요',
} as const

/**
 * 재고 부족 안내 카피 (AlertModal 1버튼) — 남은 재고 < 선택 수량.
 * 소진(0)과 달리 수량을 줄이면 신청 가능 → 모달 닫으면 현재 화면 유지(이동 X).
 */
export const NOT_ENOUGH_STOCK_DIALOG = {
  title: '남은 수량이 부족해요',
  description: '신청하신 수량보다 남은 재고가 적어요. 수량을 줄여서 다시 시도해주세요',
} as const

/** 데이터 조회 실패 시 ExitDialog 안내 카피 — 진입 게이트·상세·수량변경 공용. */
export const LOAD_ERROR_DIALOG = {
  title: '일시적인 오류가 발생했어요',
  description: '잠시 후 다시 시도해주세요',
} as const

/** 수량 변경 기한 만료 안내 카피 (Type B Dialog). */
export const CHANGE_NOT_ALLOWED_DIALOG = {
  title: '지금은 변경할 수 없어요',
  description: '변경 가능한 시간이 지나서\n신청 정보를 변경할 수 없어요',
} as const

/** 신청 취소 불가 안내 카피 (Type B Dialog) — 기한 만료·서버 422 공용. */
export const CANCEL_NOT_ALLOWED_DIALOG = {
  title: '지금은 취소할 수 없어요',
  description: '취소 가능한 시간이 지나서\n비닐 신청을 취소할 수 없어요',
} as const

/**
 * 토스/네이버 successUrl 복귀 시 sessionStorage에서 복원할 결제 컨텍스트 키.
 *
 * `/sealed-vinyl/process`에서 POST /product-payments/process body 채울 때 사용.
 * PaymentStep handlePay 직전에 저장 — 새로고침/뒤로가기에도 살아남게.
 */
export const PAYMENT_CONTEXT_STORAGE = {
  PAYMENT_ID: 'sealed-vinyl:paymentId',
  AMOUNT: 'sealed-vinyl:amount',
} as const

/** step → 헤더 타이틀 매핑 */
export const STEP_TITLE: Record<SealedVinylStep, string> = {
  landing: '밀봉비닐 나눔',
  order: '밀봉비닐 나눔',
  payment: '결제하기',
  complete: '신청 완료',
  detail: '신청 정보',
  edit: '수량 변경',
  // soldOut 은 landing 위에 딤만 씌운 화면이라 헤더(타이틀·뒤로가기)를 그대로 유지
  soldOut: '밀봉비닐 나눔',
}

/** 헤더 자체를 노출하지 않는 step (complete = 본문만) */
export const NO_HEADER_STEPS: ReadonlySet<SealedVinylStep> = new Set(['complete'])

/** 헤더 뒤로가기를 노출하지 않을 step (재진입 차단·웹뷰 종료가 자연스러운 화면) */
export const NO_BACK_STEPS: ReadonlySet<SealedVinylStep> = new Set(['complete'])

/** 주소 관련 컨펌 모달 종류 — 등록 / 변경 */
export type AddressModalKind = 'register' | 'change'

/** 주소 관련 컨펌 모달 카피 — 확인 시 앱 주소관리(my/address)로 이동 (Figma 20897-28238 / 21014-27011) */
export const ADDRESS_MODAL: Record<AddressModalKind, { title: string; description: string; primaryLabel: string }> = {
  register: {
    title: '주소 정보가 필요해요',
    description: '배송 가능 여부를 확인하기 위해 지번 주소를 등록해주세요',
    primaryLabel: '주소 등록하기',
  },
  change: {
    title: '신청이 불가능한 지역이에요',
    description: '밀봉비닐은 서비스 지역에서만 신청할 수 있어요. 대표주소를 변경해주세요',
    primaryLabel: '주소 변경하기',
  },
}
